import { inject } from '@angular/core';
import type { CanActivateFn } from '@angular/router';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth/auth.service';
import { CurrentUserService } from '../services/auth/current-user.service';

/** Roles that belong to the external portals (provisioned real accounts). */
export const CANDIDATE_ROLES = ['candidate'];
export const COMPANY_ROLES = ['company_owner', 'company_member'];
export const PORTAL_ROLES = [...CANDIDATE_ROLES, ...COMPANY_ROLES];

/** Landing route for a role: portals for external roles, backoffice for staff. */
export function homeRouteForRole(role: string | null | undefined): string {
	const name = (role ?? '').toLowerCase();
	if (CANDIDATE_ROLES.includes(name)) return '/candidate';
	if (COMPANY_ROLES.includes(name)) return '/company';
	return '/dashboard';
}

/**
 * Gates a portal route tree to its role(s). Anyone else authenticated is
 * bounced to their own home instead of seeing a foreign shell.
 */
export const portalRoleGuard = (allowedRoles: string[]): CanActivateFn => {
	return () => {
		const authService = inject(AuthService);
		const currentUserService = inject(CurrentUserService);
		const router = inject(Router);

		// Presencia, no caducidad: mismo motivo que en `authGuard`. Un token
		// vencido con refresh vivo lo arregla el interceptor en la primera
		// petición; echar al login aquí convierte una sesión recuperable en una
		// sesión perdida.
		if (!authService.getToken()) {
			router.navigate(['/login']);
			return false;
		}

		const role = currentUserService.getUserRoleName()?.toLowerCase() ?? '';
		if (allowedRoles.includes(role)) {
			return true;
		}

		router.navigate([homeRouteForRole(role)]);
		return false;
	};
};

/**
 * Keeps portal roles out of the backoffice: a candidate or company user
 * landing on /dashboard gets sent to their portal.
 */
export const staffOnlyGuard: CanActivateFn = () => {
	const currentUserService = inject(CurrentUserService);
	const router = inject(Router);

	const role = currentUserService.getUserRoleName()?.toLowerCase() ?? '';
	if (PORTAL_ROLES.includes(role)) {
		router.navigate([homeRouteForRole(role)]);
		return false;
	}
	return true;
};
